import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { RecurrenceConfigError, UnsupportedFrequencyError } from './errors';
import type { Frequency, ParsedRule } from './types';

dayjs.extend(utc);

/**
 * Exhaustive over `Frequency` by construction — widening the type must fail tsc
 * here rather than leaving the parser silently rejecting the new value.
 */
const SUPPORTED_FREQ = {
  DAILY: true,
  WEEKLY: true,
  MONTHLY: true,
  YEARLY: true,
} as const satisfies Record<Frequency, true>;

const KNOWN_KEYS = new Set(['FREQ', 'INTERVAL', 'COUNT', 'UNTIL']);

/**
 * Range guard, not a product limit. The overlap scan expands up to 49 periods, so
 * an unbounded `INTERVAL` pushes a late period past the `Date` range — an error
 * `findWindowOverlap` swallows, letting an un-expandable config save and then fail
 * forever in the poller.
 */
export const MAX_INTERVAL = 1200;

/** Cap raw user input echoed into error messages (consumers may log/render them). */
const elide = (value: string): string =>
  value.length > 64 ? `${value.slice(0, 64)}…` : value;

const isSupportedFreq = (value: string): value is Frequency =>
  Object.hasOwn(SUPPORTED_FREQ, value);

/**
 * Parse an `UNTIL` value into a calendar day at midnight UTC. Accepts iCal basic
 * (`YYYYMMDD[THHMMSSZ]`), iCal date-only (`YYYYMMDD`), or dashed ISO 8601
 * (`YYYY-MM-DD`, optional time). Any time component is discarded. Throws
 * `RecurrenceConfigError` on an unrecognised form or a non-existent calendar
 * date (e.g. `20240230`).
 */
const parseUntil = (raw: string): Date => {
  // dayjs parses non-strict, rolling overflow (Feb 30 → Mar 1). Round-trip
  // through the normalized string to reject overflow instead of shifting it.
  const validateDate = (normalized: string): Date => {
    const parsed = dayjs.utc(normalized);
    if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== normalized) {
      throw new RecurrenceConfigError(`Invalid UNTIL value: '${elide(raw)}'`);
    }

    return parsed.toDate();
  };

  // iCal basic: YYYYMMDD with an optional THHMMSS[Z] time (time discarded).
  const compact = /^(\d{4})(\d{2})(\d{2})(?:T\d{2}\d{2}\d{2}Z?)?$/;
  // Dashed ISO 8601, optional time component (discarded — UNTIL bounds a day).
  // The time, if present, must be well-formed: a garbage time is rejected, not
  // silently dropped (mirrors the compact form's strictness).
  const dashed =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?)?$/;

  const match = compact.exec(raw) ?? dashed.exec(raw);
  if (match === null) {
    throw new RecurrenceConfigError(`Invalid UNTIL value: '${elide(raw)}'`);
  }

  const [, y, mo, d] = match;

  return validateDate(`${y}-${mo}-${d}`);
};

const parseIntComponent = (key: string, raw: string): number => {
  if (!/^\d+$/.test(raw)) {
    throw new RecurrenceConfigError(
      `${key} must be a positive integer, got '${elide(raw)}'`
    );
  }

  const value = Number(raw);
  if (!Number.isSafeInteger(value)) {
    throw new RecurrenceConfigError(
      `${key} must be a safe integer, got '${elide(raw)}'`
    );
  }

  return value;
};

/**
 * Parse an RRULE string into a `ParsedRule`, validating the supported subset.
 * Throws `RecurrenceConfigError` on a structural or semantic violation, and
 * `UnsupportedFrequencyError` on a `FREQ` outside
 * {DAILY, WEEKLY, MONTHLY, YEARLY}.
 */
export const parseRecurrence = (rrule: string): ParsedRule => {
  const trimmed = rrule.trim();
  if (trimmed === '') {
    throw new RecurrenceConfigError('RRULE string is empty');
  }

  const components = new Map<string, string>();
  for (const rawToken of trimmed.split(';')) {
    const token = rawToken.trim();
    // Tolerate empty components from leading/trailing/doubled `;`.
    if (token === '') {
      continue;
    }
    const eq = token.indexOf('=');
    if (eq <= 0 || eq === token.length - 1) {
      throw new RecurrenceConfigError(
        `Malformed RRULE component (expected KEY=VALUE): '${elide(token)}'`
      );
    }
    const key = token.slice(0, eq).toUpperCase();
    const value = token.slice(eq + 1);
    if (components.has(key)) {
      throw new RecurrenceConfigError(
        `Duplicate RRULE component: '${elide(key)}'`
      );
    }
    components.set(key, value);
  }

  const freqRaw = components.get('FREQ');
  if (freqRaw === undefined) {
    throw new RecurrenceConfigError(`RRULE is missing FREQ: '${elide(rrule)}'`);
  }
  const freq = freqRaw.toUpperCase();
  if (!isSupportedFreq(freq)) {
    throw new UnsupportedFrequencyError(
      freq,
      `Unsupported FREQ '${elide(freqRaw)}' (supported: ${Object.keys(SUPPORTED_FREQ).join(', ')})`
    );
  }

  for (const key of components.keys()) {
    if (!KNOWN_KEYS.has(key)) {
      throw new RecurrenceConfigError(
        `Unsupported RRULE component: '${elide(key)}'`
      );
    }
  }

  let interval = 1;
  const intervalRaw = components.get('INTERVAL');
  if (intervalRaw !== undefined) {
    interval = parseIntComponent('INTERVAL', intervalRaw);
    if (interval < 1) {
      throw new RecurrenceConfigError(`INTERVAL must be >= 1, got ${interval}`);
    }
    if (interval > MAX_INTERVAL) {
      throw new RecurrenceConfigError(
        `INTERVAL must be <= ${MAX_INTERVAL}, got ${interval}`
      );
    }
  }

  const countRaw = components.get('COUNT');
  const untilRaw = components.get('UNTIL');
  if (countRaw !== undefined && untilRaw !== undefined) {
    throw new RecurrenceConfigError(
      'COUNT and UNTIL are mutually exclusive; only one may be set'
    );
  }

  const rule: ParsedRule = { freq, interval };

  if (countRaw !== undefined) {
    const count = parseIntComponent('COUNT', countRaw);
    if (count < 1) {
      throw new RecurrenceConfigError(`COUNT must be >= 1, got ${count}`);
    }
    rule.count = count;
  }

  if (untilRaw !== undefined) {
    rule.until = parseUntil(untilRaw);
  }

  return rule;
};
